import { Editor } from '@ckeditor/ckeditor5-core/src/editor/editor';
import React, { FC, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import TextSelectionPopover from 'react-text-selection-popover';
import styled from 'styled-components';
import { SquadData } from 'components/molecules/squad-picker/use-squad-options';
import {
  CKCollabEditor as CKCollabEditorAtom,
  EditorsObject,
} from 'components/organisms/ck-collab-editor';
import {
  DocBalloonToolbar,
  DocBalloonToolbarRef,
  SupportedBalloonToolbarOptions,
} from 'components/organisms/document/parts/doc-balloon-toolbar';
import { DocumentHead } from 'components/organisms/document/parts/document-head';
import { Task } from 'data/tasks';
import { TemplateObjectType } from 'data/templates/api';
import { useDocumentById } from 'data/templates/documents/hooks/use-document-by-id';
import { useTemplateMentionsData } from 'data/templates/hooks';
import { TaskSection } from 'vendor/ckeditor/plugins/document-tasks-section';
import { ConditionalVariables } from 'vendor/ckeditor/plugins/variables/conditional-variables';
import { SimpleVariables } from 'vendor/ckeditor/plugins/variables/simple-variables';
import { CKLinkPopover } from 'vendor/ckeditor/react-components/link-popover';
import { CKMentionPopup } from 'vendor/ckeditor/react-components/mention';
import { CKTaskPopover } from 'vendor/ckeditor/react-components/task-popover';
import { CKVariablesPopup } from 'vendor/ckeditor/react-components/variables';
import { useEditorConfigs } from './use-editor-configs';

const DocumentWrapper = styled.div<{ isDocumentTemplate: boolean }>`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 228px);
  overflow-y: scroll;
  padding-top: ${({ isDocumentTemplate }) =>
    isDocumentTemplate && 'var(--spacing-09)'};
`;
const EditorWrapper = styled.div`
  flex: 1;
`;

const CKCollabEditor = styled(CKCollabEditorAtom)`
  display: flex;
  height: 100%;

  > div,
  .editor {
    flex: 1;
    display: flex;
    flex-direction: column;

    &[class*='styles_loader'][class*='styles_ready'] {
      display: none;
    }
  }

  .ck.ck-editor__editable_inline {
    height: 100%;
  }
`;

const RefMentionPopup = styled.div`
  z-index: var(--zindex-popover);
  position: fixed;
`;

export type TemplateEditorProps = {
  readOnly?: boolean;
};

export const TemplateRocketlaneDocumentEditor: FC<{
  activeEditor: Editor;
  assignee: SquadData;
  docId: number;
  privateSectionEnabled: boolean;
  editorProps?: TemplateEditorProps;
  onInitEditorInstance(editors: EditorsObject): void;
  onEditorFocusChange(editor: Editor): void;
  onTogglePrivateSection(state: boolean): void;
  onUpdateDocumentTitle(title: string): void;
  onAddHighlightTask(task: Partial<Task>): number;
}> = ({
  activeEditor,
  assignee,
  docId,
  privateSectionEnabled,
  editorProps,
  onInitEditorInstance,
  onEditorFocusChange,
  onTogglePrivateSection,
  onUpdateDocumentTitle,
  onAddHighlightTask,
}) => {
  const { templateId } = useParams<{ templateId: string }>();
  const documentTemplate = useDocumentById(docId);
  const { apiService, extraConfig } = useEditorConfigs({
    docId,
    readOnly: editorProps?.readOnly,
  });
  const [documentTitle, setDocumentTitle] = useState(
    documentTemplate?.templateName || '',
  );

  const mentionData = useTemplateMentionsData();

  const presenceListElementRef = useRef<HTMLDivElement>(null);
  const docRef = useRef<HTMLInputElement | null>(null);
  const refBalloonPopup = useRef<HTMLDivElement | null>(null);
  const refMentionPopup = useRef<HTMLDivElement | null>(null);

  const [mentionPopupPosition, setMentionPopupPosition] = useState({
    top: 0,
    left: 0,
  });

  const updateDocumentTitle = () => {
    onUpdateDocumentTitle(documentTitle);
  };

  const { pathname } = useLocation();
  const isDocumentTemplate = pathname.includes('/documents');

  return (
    <DocumentWrapper isDocumentTemplate={isDocumentTemplate}>
      <DocumentHead
        onChange={setDocumentTitle}
        onEnter={() => {
          activeEditor?.editing.view.focus();

          updateDocumentTitle();
        }}
        onBlur={updateDocumentTitle}
        title={documentTitle}
        readOnly={editorProps?.readOnly}
      />

      <CKLinkPopover docRef={docRef} />

      <CKTaskPopover
        assignee={assignee}
        mentionData={mentionData}
        canAddDate={false}
        docRef={docRef}
        docId={docId}
        onSubmit={(task) => onAddHighlightTask(task)}
      />

      <CKMentionPopup
        projectId={Number(templateId)}
        attachTo={refMentionPopup.current}
        editor={activeEditor}
        updateParentElementLocation={(position) => {
          setMentionPopupPosition({
            left: position.left,
            top: position.top,
          });
        }}
        mentionData={useTemplateMentionsData()}
      >
        <RefMentionPopup
          ref={refMentionPopup}
          style={{
            top: mentionPopupPosition.top,
            left: mentionPopupPosition.left,
          }}
        />
      </CKMentionPopup>

      <CKVariablesPopup
        editor={activeEditor}
        attachTo={refMentionPopup.current}
        updateParentElementLocation={(position) => {
          setMentionPopupPosition({
            left: position.left,
            top: position.top,
          });
        }}
        templateType={TemplateObjectType.Document}
        allowConditionalVariables
      >
        <RefMentionPopup
          ref={refMentionPopup}
          style={{
            top: mentionPopupPosition.top,
            left: mentionPopupPosition.left,
          }}
        />
      </CKVariablesPopup>

      {activeEditor ? (
        <>
          <DocBalloonToolbarRef ref={refBalloonPopup} />
          <TextSelectionPopover
            selectionRef={docRef}
            containerNode={refBalloonPopup.current}
          >
            <DocBalloonToolbar
              balloonToolOptions={[
                SupportedBalloonToolbarOptions.Link,
                SupportedBalloonToolbarOptions.Task,
              ]}
              editor={activeEditor}
            />
          </TextSelectionPopover>
        </>
      ) : null}

      <EditorWrapper ref={docRef}>
        <CKCollabEditor
          apiService={apiService}
          singleUserMode
          customClasses={{ editorClass: 'editor' }}
          docId={docId}
          extraConfig={extraConfig}
          extraPlugins={[TaskSection, SimpleVariables, ConditionalVariables]}
          presenceListElementRef={presenceListElementRef}
          readOnly={editorProps?.readOnly}
          onEditorReady={onInitEditorInstance}
          onEditorFocusChange={onEditorFocusChange}
          isPrivateSectionEnabled={privateSectionEnabled}
          emitPrivateSectionStatus={(val) => {
            onTogglePrivateSection(val);
          }}
        />
      </EditorWrapper>
    </DocumentWrapper>
  );
};
